// Design-bridge hooks (RFC-104 P4).
//
// v1 (Phase 1): useTheme, useReducedMotion, useDensity, useViewport.
// v1.1 (Phase 2 foundation dispatch — this file): useDebounced,
// useStateNarration, useRovingTabindex, useAuditWriter, useDefenceVariant.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { tokens, type Tokens } from './tokens';

export type ThemeMode = 'dark' | 'light';
export type Density = 'comfortable' | 'compact';
export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';

export interface ThemeApi {
  readonly mode: ThemeMode;
  readonly tokens: Tokens;
  toggle(): void;
}

/**
 * Phase 2 v1: dark-only theme. Toggle is a no-op until Phase 3 ships light values.
 * Consumers may safely call `mode` and `toggle()` today; the swap is a future
 * implementation detail behind this hook.
 */
export function useTheme(): ThemeApi {
  return {
    mode: 'dark',
    tokens,
    toggle: () => {
      /* phase 3: light mode */
    },
  };
}

/**
 * Mirrors `prefers-reduced-motion: reduce`. Returns true if the user has
 * reduced motion on; consumers clamp their durations to
 * `tokens.motion.reducedMotion.duration` (0 ms).
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14 fallback
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return reduced;
}

/**
 * Density preference. Phase 2 v1 always returns 'comfortable'; setting/
 * preference UI lands in a future PR.
 */
export function useDensity(): Density {
  return 'comfortable';
}

export interface ViewportInfo {
  readonly w: number;
  readonly h: number;
  readonly breakpoint: Breakpoint;
}

function pickBreakpoint(w: number): Breakpoint {
  if (w < tokens.breakpoint.sm) return 'sm';
  if (w < tokens.breakpoint.md) return 'md';
  if (w < tokens.breakpoint.lg) return 'lg';
  return 'xl';
}

export function useViewport(): ViewportInfo {
  const initial =
    typeof window === 'undefined'
      ? { w: tokens.breakpoint.lg, h: 800 }
      : { w: window.innerWidth, h: window.innerHeight };
  const [vp, setVp] = useState<{ w: number; h: number }>(initial);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return { w: vp.w, h: vp.h, breakpoint: pickBreakpoint(vp.w) };
}

// ===========================================================================
// useDebounced — Phase 2 foundation
// ===========================================================================
//
// Standard debounce hook. Respects `prefers-reduced-motion: reduce`: when
// reduced, the hook is instant (returns `value` synchronously) so screen-
// reader announcements do not stack up behind a debounce timer.
//
// Used by GVW-030 / GVW-050 / GVW-070 / GVW-080 description regions
// (1 s polite debounce per a11y-plan.md §C.4.3 / §F.2 / §E.2).

export function useDebounced<T>(value: T, ms: number): T {
  const reduced = useReducedMotion();
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    if (reduced || ms <= 0) {
      setDebounced(value);
      return;
    }
    const handle = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(handle);
  }, [value, ms, reduced]);
  return debounced;
}

// ===========================================================================
// useStateNarration — Phase 2 foundation
// ===========================================================================
//
// Implements the CP-4.2 alert→status flip pattern.
//
//   First occurrence per (key, session) → role="alert"  + aria-live="assertive"
//   Subsequent                          → role="status" + aria-live="polite"
//
// Persistence: keyed by `gv:state-narration:<key>` in `sessionStorage`.
// `severity="critical"` is always assertive (never flips).

export type StateNarrationSeverity = 'info' | 'success' | 'warning' | 'error' | 'critical';

export interface UseStateNarrationArgs {
  key: string;
  severity: StateNarrationSeverity;
  /** Used to detect text changes so the assertive→polite flip applies per text occurrence. */
  text?: string;
}

export interface UseStateNarrationReturn {
  role: 'status' | 'alert';
  ariaLive: 'polite' | 'assertive';
}

const STORAGE_PREFIX = 'gv:state-narration:';

export function useStateNarration({
  key,
  severity,
  text,
}: UseStateNarrationArgs): UseStateNarrationReturn {
  const isAssertiveSeverity = severity === 'error' || severity === 'critical' || severity === 'warning';
  const storageKey = `${STORAGE_PREFIX}${key}`;
  const fingerprint = `${severity}|${text ?? ''}`;

  const seen = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(storageKey) === fingerprint;
    } catch {
      return false;
    }
  }, [storageKey, fingerprint]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(storageKey, fingerprint);
    } catch {
      /* storage may be disabled (private mode); fail-open as polite */
    }
  }, [storageKey, fingerprint]);

  // Critical: always assertive
  if (severity === 'critical') return { role: 'alert', ariaLive: 'assertive' };

  // First time this (key, fingerprint) pair appears → assertive (only when severity is interruption-worthy)
  if (!seen && isAssertiveSeverity) return { role: 'alert', ariaLive: 'assertive' };

  return { role: 'status', ariaLive: 'polite' };
}

// ===========================================================================
// useRovingTabindex — Phase 2 foundation
// ===========================================================================
//
// Closes A11y QA §T.1 / §C.3 / §I.3 / §M.1 (every list / tablist / toolbar
// / menubar must roll tabindex across items).
//
// API:
//   const r = useRovingTabindex({ count: 5, orientation: 'horizontal' });
//   <ul role="tablist">
//     {items.map((item, i) => (
//       <li {...r.getItemProps(i)} role="tab">…</li>
//     ))}
//   </ul>

export type RovingOrientation = 'vertical' | 'horizontal' | 'both';

export interface UseRovingTabindexArgs {
  count: number;
  orientation?: RovingOrientation;
  initialIndex?: number;
  /** Called when the user activates the item with Enter / Space. */
  onActivate?(index: number): void;
  /** When true, wrap from end to start and vice versa. Default true. */
  loop?: boolean;
}

export interface RovingItemProps {
  tabIndex: number;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  onFocus: () => void;
  ref: (node: HTMLElement | null) => void;
}

export interface UseRovingTabindexReturn {
  activeIndex: number;
  setActiveIndex(next: number): void;
  getItemProps(index: number): RovingItemProps;
}

export function useRovingTabindex({
  count,
  orientation = 'vertical',
  initialIndex = 0,
  onActivate,
  loop = true,
}: UseRovingTabindexArgs): UseRovingTabindexReturn {
  const [activeIndex, setActiveIndexState] = useState<number>(() =>
    Math.max(0, Math.min(count - 1, initialIndex)),
  );
  const itemsRef = useRef<Array<HTMLElement | null>>([]);

  const setActiveIndex = useCallback(
    (next: number) => {
      if (count <= 0) return;
      let resolved = next;
      if (loop) {
        if (resolved < 0) resolved = count - 1;
        if (resolved >= count) resolved = 0;
      } else {
        resolved = Math.max(0, Math.min(count - 1, resolved));
      }
      setActiveIndexState(resolved);
      // Move focus to the new active item if currently any item has focus.
      const node = itemsRef.current[resolved];
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      const focusInside = active && itemsRef.current.some((el) => el === active);
      if (focusInside && node) node.focus();
    },
    [count, loop],
  );

  const getItemProps = useCallback(
    (index: number): RovingItemProps => ({
      tabIndex: index === activeIndex ? 0 : -1,
      ref: (node: HTMLElement | null) => {
        itemsRef.current[index] = node;
      },
      onFocus: () => setActiveIndexState(index),
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        const nextKey = orientation === 'horizontal' ? 'ArrowRight' : orientation === 'vertical' ? 'ArrowDown' : null;
        const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : orientation === 'vertical' ? 'ArrowUp' : null;
        if (orientation === 'both') {
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            e.preventDefault();
            setActiveIndex(index + 1);
            return;
          }
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            e.preventDefault();
            setActiveIndex(index - 1);
            return;
          }
        } else {
          if (e.key === nextKey) {
            e.preventDefault();
            setActiveIndex(index + 1);
            return;
          }
          if (e.key === prevKey) {
            e.preventDefault();
            setActiveIndex(index - 1);
            return;
          }
        }
        if (e.key === 'Home') {
          e.preventDefault();
          setActiveIndex(0);
          return;
        }
        if (e.key === 'End') {
          e.preventDefault();
          setActiveIndex(count - 1);
          return;
        }
        if ((e.key === 'Enter' || e.key === ' ') && onActivate) {
          e.preventDefault();
          onActivate(index);
        }
      },
    }),
    [activeIndex, orientation, count, setActiveIndex, onActivate],
  );

  return { activeIndex, setActiveIndex, getItemProps };
}

// ===========================================================================
// useAuditWriter — Phase 2 foundation
// ===========================================================================
//
// Stub-friendly hook that wraps the audit client (state/auditClient.ts).
// Returns a typed callback that components await; the underlying client
// queues offline rows in sessionStorage if the backend is unreachable.
//
// Cites BACKEND_NOTES.md BE-020 (audit table) + BE-021 (audit retention).
// The backend endpoint is `/api/v1/audit/rows` (BE-021 floor: 365 days for
// defence variant per boss-synthesis.md §11.7).

export interface AuditEvent {
  /** Stable event family, e.g. `gvw.share.revoke`, `gvw.layer.add`. */
  kind: string;
  /** Arbitrary structured payload (must be JSON-serialisable). */
  payload?: Record<string, unknown>;
  /** Optional user-facing error code emitted by the same flow. */
  errorCode?: string;
  /** Optional resource tracker for the audit-deep-link (e.g. layer id). */
  resourceId?: string;
}

export type AuditWriter = (event: AuditEvent) => Promise<void>;

let _writer: AuditWriter | null = null;
let _writerLoader: Promise<AuditWriter> | null = null;

async function loadWriter(): Promise<AuditWriter> {
  if (_writer) return _writer;
  if (_writerLoader) return _writerLoader;
  // Lazy import to keep the design bridge from forcing the audit client into
  // every chunk that imports the bridge.
  _writerLoader = import('@/state/auditClient').then((m) => {
    _writer = m.appendAuditRow;
    return m.appendAuditRow;
  });
  return _writerLoader;
}

export function useAuditWriter(): AuditWriter {
  return useCallback(async (event: AuditEvent) => {
    const writer = await loadWriter();
    return writer(event);
  }, []);
}

// ===========================================================================
// useDefenceVariant — Phase 2 foundation
// ===========================================================================
//
// Single source of truth for the defence-variant capability flag. Reads
// from a build-time Vite `define` (`__DEFENCE_VARIANT__`) AND a tenant
// runtime entitlement (delivered from the platform-sdk). Filters the
// allow-list of providers/integrations.

export interface DefenceVariantState {
  active: boolean;
  reason?: string;
  allowProvider(id: string): boolean;
}

let _defenceState: DefenceVariantState | null = null;
let _defenceLoader: Promise<DefenceVariantState> | null = null;

async function loadDefenceState(): Promise<DefenceVariantState> {
  if (_defenceState) return _defenceState;
  if (_defenceLoader) return _defenceLoader;
  _defenceLoader = import('@/state/defenceVariant').then((m) => {
    _defenceState = m.getDefenceVariantState();
    return _defenceState;
  });
  return _defenceLoader;
}

export function useDefenceVariant(): DefenceVariantState {
  // Use suspense-free resolution: synchronous reads return the last cached
  // state; the first read (cold) loads the module and returns a "safe"
  // default while the load resolves. Most consumers only need the booleans.
  const [state, setState] = useState<DefenceVariantState>(() =>
    _defenceState ?? {
      active: false,
      reason: 'loading',
      allowProvider: () => true,
    },
  );

  useEffect(() => {
    let cancelled = false;
    void loadDefenceState().then((s) => {
      if (!cancelled) setState(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
